import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Button, Spinner } from '../../components/design-system';
import { AdminPage } from './AdminPage';
import type { TwoFaSummaryRow } from '../../lib/adminUsers';

type Group = 'admin' | 'host' | 'traveler';

interface Settings {
  require_2fa_admins_at: string | null;
  require_2fa_hosts_at: string | null;
  require_2fa_travelers_at: string | null;
  two_fa_grace_days: number;
}

const GROUPS: { key: Group; label: string; column: keyof Settings; blurb: string }[] = [
  { key: 'admin', label: 'Admins', column: 'require_2fa_admins_at', blurb: 'Start here: admins can see and change everything.' },
  { key: 'host', label: 'Hosts', column: 'require_2fa_hosts_at', blurb: 'Hosts hold payout details and talk to travelers.' },
  { key: 'traveler', label: 'Travelers', column: 'require_2fa_travelers_at', blurb: 'The largest group: switch it on last.' },
];

function day(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function deadlineOf(since: string, graceDays: number): string {
  return new Date(new Date(since).getTime() + graceDays * 86_400_000).toISOString();
}

/** Admin → Security: require two-factor authentication, group by group, and see who still has to set it up. */
export function AdminSecurity() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [summary, setSummary] = useState<TwoFaSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState<Group | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [grace, setGrace] = useState('7');

  const load = useCallback(async () => {
    const [{ data: s }, { data: sum }] = await Promise.all([
      supabase.from('platform_settings')
        .select('require_2fa_admins_at, require_2fa_hosts_at, require_2fa_travelers_at, two_fa_grace_days')
        .limit(1).single(),
      supabase.rpc('admin_two_fa_summary'),
    ]);
    if (s) {
      setSettings(s as Settings);
      setGrace(String((s as Settings).two_fa_grace_days));
    }
    setSummary((sum as TwoFaSummaryRow[] | null) ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function setRequirement(group: Group, on: boolean) {
    setBusy(group);
    setError(null);
    setNotice(null);
    const { error: err } = await supabase.rpc('admin_set_2fa_requirement', { p_group: group, p_on: on });
    setBusy(null);
    if (err) { setError(err.message || "We couldn't change that."); return; }
    setConfirming(null);
    setNotice(on ? 'Required. Everyone in that group without 2FA now has the grace period.' : 'No longer required for that group.');
    await load();
  }

  async function saveGrace() {
    setBusy('grace');
    setError(null);
    setNotice(null);
    const { error: err } = await supabase.rpc('admin_set_2fa_grace_days', { p_days: Number(grace) });
    setBusy(null);
    if (err) { setError(err.message || "We couldn't save the grace period."); return; }
    setNotice('Grace period saved.');
    await load();
  }

  if (loading || !settings) {
    return <AdminPage title="Security"><div className="flex justify-center py-12"><Spinner /></div></AdminPage>;
  }

  const rowFor = (g: Group) => summary.find((r) => r.group_name === g);
  const graceNumber = Number(grace);
  const graceValid = Number.isInteger(graceNumber) && graceNumber >= 1 && graceNumber <= 30;

  return (
    <AdminPage title="Security" description="Require two-factor authentication, one group at a time.">
      {notice && <p role="status" className="mb-3 text-sm text-emerald-500">{notice}</p>}
      {error && <p role="alert" className="mb-3 text-sm text-crimson">{error}</p>}

      <div className="flex flex-col gap-4 max-w-3xl">
        {GROUPS.map((g) => {
          const since = settings[g.column] as string | null;
          const row = rowFor(g.key);
          const todo = row ? row.total - row.with_two_fa : 0;
          const on = since !== null;
          return (
            <section key={g.key} className="bg-app-card rounded-xl border border-app-border p-5 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-app-fg uppercase tracking-wide">{g.label}</h2>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${on ? 'bg-emerald-500/15 text-emerald-500' : 'bg-app-border text-app-muted'}`}>
                  {on ? 'Required' : 'Not required'}
                </span>
              </div>
              <p className="text-xs text-app-muted">{g.blurb}</p>
              {row && (
                <p className="text-sm text-app-fg">
                  {row.with_two_fa} of {row.total} have 2FA
                  {todo > 0 && <> · <Link to={`/admin/users?role=${g.key}&twofa=off`} className="text-gold underline underline-offset-2">{todo} still to do</Link></>}
                </p>
              )}
              {on && (
                <p className="text-xs text-app-muted">
                  Required since {day(since)}. Anyone without 2FA must set it up by {day(deadlineOf(since, settings.two_fa_grace_days))}
                  {' '}(new accounts get {settings.two_fa_grace_days} days from the day they join).
                </p>
              )}

              {!on && confirming !== g.key && (
                <div><Button size="sm" variant="secondary" onClick={() => { setConfirming(g.key); setError(null); setNotice(null); }}>Require 2FA for {g.label.toLowerCase()}…</Button></div>
              )}
              {!on && confirming === g.key && (
                <div role="alert" className="rounded-lg border border-gold/30 bg-gold/10 p-3 text-sm text-app-fg flex flex-col gap-2">
                  <p>
                    {todo} {todo === 1 ? 'person' : 'people'} without 2FA will be asked to set it up within {settings.two_fa_grace_days} days. After that they can
                    only use the setup screen until they have done it.
                    {g.key === 'admin' && ' Set up your own 2FA first.'}
                    {' '}You can switch it off again at any time.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => void setRequirement(g.key, true)} disabled={busy !== null}>
                      {busy === g.key ? <Spinner size="sm" className="text-ink" /> : 'Require it'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirming(null)} disabled={busy !== null}>Cancel</Button>
                  </div>
                </div>
              )}
              {on && (
                <div><Button size="sm" variant="secondary" onClick={() => void setRequirement(g.key, false)} disabled={busy !== null}>
                  {busy === g.key ? <Spinner size="sm" /> : 'Stop requiring it'}
                </Button></div>
              )}
            </section>
          );
        })}

        <section className="bg-app-card rounded-xl border border-app-border p-5 flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-app-fg uppercase tracking-wide">Grace period</h2>
          <p className="text-xs text-app-muted">
            How long people have to set it up after it is required of them. A push and an email go out 3 days before their deadline.
            Changing it moves the deadline of everyone in a required group.
          </p>
          <div className="flex items-end gap-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="grace-days" className="text-sm text-app-muted">Days</label>
              <input id="grace-days" type="number" min={1} max={30} value={grace} onChange={(e) => setGrace(e.target.value)}
                className="w-24 bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-fg" />
            </div>
            <Button size="sm" onClick={() => void saveGrace()} disabled={busy !== null || !graceValid || graceNumber === settings.two_fa_grace_days}>
              {busy === 'grace' ? <Spinner size="sm" className="text-ink" /> : 'Save'}
            </Button>
          </div>
        </section>

        <p className="text-xs text-app-muted">
          Someone who loses their phone can be reset from Admin → Users (open the person, then Reset 2FA).
        </p>
      </div>
    </AdminPage>
  );
}

export default AdminSecurity;
